import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { DashboardLayout } from './components/layouts/DashboardLayout';
import { PortalLayout } from './components/layouts/PortalLayout';
import { AuthLayout } from './components/layouts/AuthLayout';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { PrivateRoute } from './routes/PrivateRoute';
import { PortalRoute } from './routes/PortalRoute';

// =====================================================
// PÁGINAS PRINCIPAIS - Import direto (sem lazy loading)
// Estas são as páginas mais acessadas, carregam no bundle inicial
// =====================================================
import DashboardPage from './pages/dashboard/DashboardPage';
import PatientsListPage from './pages/patients/PatientsListPage';
import PatientDetailPage from './pages/patients/PatientDetailPage/PatientDetailPage';
import ClinicalNotesPage from './pages/clinical/ClinicalNotesPage';
import DemandsQueuePage from './pages/demands/DemandsQueuePage';
import TasksPage from './pages/tasks/TasksPage';
import QuestionnairesPage from './pages/questionnaires/QuestionnairesPage';
import CareLinesPage from './pages/care-lines/CareLinesPage';
import MessagesPage from './pages/messages/MessagesPage';
import EncountersPage from './pages/encounters/EncountersPage';

// =====================================================
// PÁGINAS SECUNDÁRIAS - Lazy loading
// Carregam sob demanda para reduzir bundle inicial
// =====================================================
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const NewPatientPage = lazy(() => import('./pages/patients/NewPatientPage'));
const EditPatientPage = lazy(() => import('./pages/patients/EditPatientPage'));
const CareLineDetailPage = lazy(() => import('./pages/care-lines/CareLineDetailPage'));
const QuestionnaireResponsePage = lazy(() => import('./pages/questionnaires/QuestionnaireResponsePage'));
const SendQuestionnairePage = lazy(() => import('./pages/questionnaires/SendQuestionnairePage'));
const DemandDetailPage = lazy(() => import('./pages/demands/DemandDetailPage'));
const TaskDetailPage = lazy(() => import('./pages/tasks/TaskDetailPage'));
const MessageDetailPage = lazy(() => import('./pages/messages/MessageDetailPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const NewClinicalNotePage = lazy(() => import('./pages/clinical/NewClinicalNotePage'));
const ClinicalNoteDetailPage = lazy(() => import('./pages/clinical/ClinicalNoteDetailPage'));
const EditClinicalNotePage = lazy(() => import('./pages/clinical/EditClinicalNotePage'));
const NewEncounterPage = lazy(() => import('./pages/encounters/NewEncounterPage'));
const EncounterDetailPage = lazy(() => import('./pages/encounters/EncounterDetailPage'));
const RiskOverviewPage = lazy(() => import('./pages/risk/RiskOverviewPage'));
const NewRiskAssessmentPage = lazy(() => import('./pages/risk/NewRiskAssessmentPage'));
const RiskAssessmentDetailPage = lazy(() => import('./pages/risk/RiskAssessmentDetailPage'));
const CarePlansPage = lazy(() => import('./pages/careplans/CarePlansPage'));
const NewCarePlanPage = lazy(() => import('./pages/careplans/NewCarePlanPage'));
const CarePlanDetailPage = lazy(() => import('./pages/careplans/CarePlanDetailPage'));
const CarePlanEditPage = lazy(() => import('./pages/careplans/CarePlanEditPage'));
const EducationPage = lazy(() => import('./pages/education/EducationPage'));
const ContentFormPage = lazy(() => import('./pages/education/ContentFormPage'));
const ContentDetailPage = lazy(() => import('./pages/education/ContentDetailPage'));
const CampaignsPage = lazy(() => import('./pages/campaigns/CampaignsPage'));
const CampaignDetailPage = lazy(() => import('./pages/campaigns/CampaignDetailPage'));
const FunnelPage = lazy(() => import('./pages/funnel/FunnelPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));

// Executive & Analytics pages
const ExecutiveDashboardPage = lazy(() => import('./pages/executive/ExecutiveDashboardPage').then(m => ({ default: m.ExecutiveDashboardPage })));
const ABTestsPage = lazy(() => import('./pages/ab-tests/ABTestsPage').then(m => ({ default: m.ABTestsPage })));
const ABTestCreatePage = lazy(() => import('./pages/ab-tests/ABTestCreatePage').then(m => ({ default: m.ABTestCreatePage })));
const ABTestDetailPage = lazy(() => import('./pages/ab-tests/ABTestDetailPage').then(m => ({ default: m.ABTestDetailPage })));

// Admin pages
const LGPDAdminPage = lazy(() => import('./pages/admin/LGPDAdminPage'));
const BIAdminPage = lazy(() => import('./pages/admin/BIAdminPage'));
const TasyIntegrationPage = lazy(() => import('./pages/admin/TasyIntegrationPage'));
const InactivatedPatientsPage = lazy(() => import('./pages/admin/InactivatedPatientsPage'));
const IntegrationHubPage = lazy(() => import('./pages/admin/IntegrationHubPage'));
const BulkImportPage = lazy(() => import('./pages/admin/BulkImportPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));

// Custom Dashboards
const CustomDashboardsPage = lazy(() => import('./pages/dashboards/CustomDashboardsPage'));
const CustomDashboardViewPage = lazy(() => import('./pages/dashboards/CustomDashboardViewPage'));
const CustomDashboardEditorPage = lazy(() => import('./pages/dashboards/CustomDashboardEditorPage'));

// Insights
const CommunicationInsightsPage = lazy(() => import('./pages/insights/CommunicationInsightsPage'));

// BI - Metabase
const MetabaseDashboardsPage = lazy(() => import('./pages/bi/MetabaseDashboardsPage'));

// Portal pages
const PortalLoginPage = lazy(() => import('./pages/portal/PortalLoginPage'));
const PortalHomePage = lazy(() => import('./pages/portal/PortalHomePage'));
const PortalQuestionnairePage = lazy(() => import('./pages/portal/PortalQuestionnairePage'));
const PortalContentPage = lazy(() => import('./pages/portal/PortalContentPage'));
const PortalDemandPage = lazy(() => import('./pages/portal/PortalDemandPage'));
const PortalLunaPage = lazy(() => import('./pages/portal/PortalLunaPage'));
const PortalDocumentsPage = lazy(() => import('./pages/portal/PortalDocumentsPage'));
const PortalPrivacyPage = lazy(() => import('./pages/portal/PortalPrivacyPage'));
const PortalCarePlansPage = lazy(() => import('./pages/portal/PortalCarePlansPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function App() {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Dashboard Routes (Protected) */}
        <Route
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/patients" element={<PatientsListPage />} />
          <Route path="/patients/new" element={<NewPatientPage />} />
          <Route path="/patients/:id/edit" element={<EditPatientPage />} />
          <Route path="/patients/:id/*" element={<PatientDetailPage />} />
          <Route path="/care-lines" element={<CareLinesPage />} />
          <Route path="/care-lines/:id" element={<CareLineDetailPage />} />
          <Route path="/questionnaires" element={<QuestionnairesPage />} />
          <Route path="/questionnaires/send" element={<SendQuestionnairePage />} />
          <Route path="/questionnaires/responses/:id" element={<QuestionnaireResponsePage />} />
          <Route path="/demands" element={<DemandsQueuePage />} />
          <Route path="/demands/:id" element={<DemandDetailPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/:id" element={<TaskDetailPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:id" element={<MessageDetailPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/clinical" element={<ClinicalNotesPage />} />
          <Route path="/clinical/new" element={<NewClinicalNotePage />} />
          <Route path="/clinical/:id" element={<ClinicalNoteDetailPage />} />
          <Route path="/clinical/:id/edit" element={<EditClinicalNotePage />} />
          <Route path="/encounters" element={<EncountersPage />} />
          <Route path="/encounters/new" element={<NewEncounterPage />} />
          <Route path="/encounters/:id" element={<EncounterDetailPage />} />
          <Route path="/risk" element={<RiskOverviewPage />} />
          <Route path="/risk/new" element={<NewRiskAssessmentPage />} />
          <Route path="/risk/:id" element={<RiskAssessmentDetailPage />} />
          <Route path="/care-plans" element={<CarePlansPage />} />
          <Route path="/care-plans/new" element={<NewCarePlanPage />} />
          <Route path="/care-plans/:id" element={<CarePlanDetailPage />} />
          <Route path="/care-plans/:id/edit" element={<CarePlanEditPage />} />
          <Route path="/education" element={<EducationPage />} />
          <Route path="/education/new" element={<ContentFormPage />} />
          <Route path="/education/:id" element={<ContentDetailPage />} />
          <Route path="/education/:id/edit" element={<ContentFormPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="/funnel" element={<FunnelPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* Analytics & A/B Tests */}
          <Route path="/executive" element={<ExecutiveDashboardPage />} />
          <Route path="/ab-tests" element={<ABTestsPage />} />
          <Route path="/ab-tests/new" element={<ABTestCreatePage />} />
          <Route path="/ab-tests/:id" element={<ABTestDetailPage />} />
          {/* Custom Dashboards */}
          <Route path="/dashboards" element={<CustomDashboardsPage />} />
          <Route path="/dashboards/new" element={<CustomDashboardEditorPage />} />
          <Route path="/dashboards/:id" element={<CustomDashboardViewPage />} />
          <Route path="/dashboards/:id/edit" element={<CustomDashboardEditorPage />} />
          {/* Insights */}
          <Route path="/insights/communication" element={<CommunicationInsightsPage />} />
          {/* BI - Metabase */}
          <Route path="/bi/metabase" element={<MetabaseDashboardsPage />} />
          {/* Admin */}
          <Route path="/admin/lgpd" element={<LGPDAdminPage />} />
          <Route path="/admin/bi" element={<BIAdminPage />} />
          <Route path="/admin/tasy" element={<TasyIntegrationPage />} />
          <Route path="/admin/inactivated-patients" element={<InactivatedPatientsPage />} />
          <Route path="/admin/integration-hub" element={<IntegrationHubPage />} />
          <Route path="/admin/bulk-import" element={<BulkImportPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
        </Route>

        {/* Portal Routes (Patient) */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        {/* Public questionnaire route (no auth required) */}
        <Route path="/q/:token" element={<PortalQuestionnairePage />} />
        {/* Public content route (no auth required) */}
        <Route path="/c/:token" element={<PortalContentPage />} />
        <Route
          element={
            <PortalRoute>
              <PortalLayout />
            </PortalRoute>
          }
        >
          <Route path="/portal" element={<PortalHomePage />} />
          <Route path="/portal/questionnaire/:token" element={<PortalQuestionnairePage />} />
          <Route path="/portal/content/:token" element={<PortalContentPage />} />
          <Route path="/portal/demands" element={<PortalDemandPage />} />
          <Route path="/portal/demands/:token" element={<PortalDemandPage />} />
          <Route path="/portal/luna" element={<PortalLunaPage />} />
          <Route path="/portal/documents" element={<PortalDocumentsPage />} />
          <Route path="/portal/privacy" element={<PortalPrivacyPage />} />
          <Route path="/portal/care-plans" element={<PortalCarePlansPage />} />
        </Route>
        {/* Public portal demand route */}
        <Route path="/d/:token" element={<PortalDemandPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
